import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  desafioAtivo, faltaParaAcabar, formatIntervalo, formatPoints, formatRating, janelaDoPeriodo,
  pontosNoPeriodo, resolveConfig, semanaDe,
  interacaoDe,
} from "@nhomi/core";
import { CartaoDoCampeao, useTemporadaVigente } from "@/ui/Campeao";
import { useSession } from "@/lib/auth";
import { watchChallenge, type Challenge } from "@/data/challenges";
import { watchMembers, type Member } from "@/data/members";
import { dayKey, periodPlates, type Plate } from "@/data/plates";
import { Avatar, Empty } from "@/ui/primitives";
import { EsqueletoDePlacar } from "@/ui/Esqueleto";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { IconBack, IconBolt, IconCalendar, IconFork, IconMedal } from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";
import { Anuncio } from "@/ui/Anuncio";

/* ============================================================
   Classificações

   Três placares diferentes, como no GymEats: dias ativos, pontos e
   pratos. Não são o mesmo ranking com outro rótulo — quem cozinha pouco
   mas posta todo dia lidera "Dias" e não lidera "Pontos", e é essa
   diferença que dá assunto no grupo.

   O de PRATOS é dos pratos, não das pessoas: melhores e piores por nota.

   Todo número sai dos pratos do período, não da ficha guardada. A ficha
   soma o desafio inteiro; filtrar por semana lendo `totalPoints` daria
   um recorte que não existe.
   ============================================================ */

const PERIODOS = [
  { id: "week", label: "Semana" },
  { id: "month", label: "Mês" },
  { id: "year", label: "Ano" },
  { id: "all", label: "Todas" },
] as const;
type Periodo = typeof PERIODOS[number]["id"];

const PLACARES = [
  { id: "dias", label: "Dias", Icone: IconCalendar },
  { id: "pontos", label: "Pontos", Icone: IconBolt },
  { id: "pratos", label: "Pratos", Icone: IconMedal },
] as const;
type Placar = typeof PLACARES[number]["id"];

/** Quantas notas um prato precisa pra entrar no pódio. */
const MIN_NOTAS = 2;
/** Abaixo disto, o prato entra no Rango da Vergonha. */
const VERGONHA_ABAIXO = 5;


const rotuloDia = (dia: string) => {
  const hoje = dayKey();
  if (dia === hoje) return "hoje";
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  if (dia === dayKey(ontem)) return "ontem";
  const [, m, d] = dia.split("-");
  return `${d}/${m}`;
};

export function Standings() {
  const { cid = "" } = useParams();
  const { user } = useSession();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [plates, setPlates] = useState<Plate[] | null>(null);
  const [periodo, setPeriodo] = useState<Periodo>("week");
  const [placar, setPlacar] = useState<Placar>("pontos");
  const [abertoTudo, setAbertoTudo] = useState(false);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);
  const vigente = useTemporadaVigente(cid);
  const coroados = new Set(vigente?.campeoes ?? []);

  const janela = useMemo(() => janelaDoPeriodo(periodo, dayKey(), challenge), [periodo, challenge]);

  useEffect(() => {
    let vivo = true;
    setPlates(null);
    void periodPlates(cid, janela.de, janela.ate)
      .then((l) => { if (vivo) setPlates(l); })
      .catch(() => { if (vivo) setPlates([]); });
    return () => { vivo = false; };
  }, [cid, janela.de, janela.ate]);

  const config = useMemo(() => resolveConfig(challenge), [challenge]);

  const pessoas = useMemo(() => {
    if (!plates) return null;
    return members.map((m) => {
      const meus = plates.filter((p) => p.uid === m.uid);
      /* Os pontos saem da ficha (`dayPoints` + cravadas dos dias do
         período), a mesma conta da temporada e do total — antes eram
         refeitos dos pratos aqui e deixavam de fora cravadas e missões. */
      const pontos = periodo === "all"
        ? (m.totalPoints ?? 0)
        : pontosNoPeriodo({ uid: m.uid, name: m.name, dayPoints: m.dayPoints, extraPoints: m.extraPoints }, janela.de, janela.ate);
      const dias = new Set(meus.map((p) => p.day)).size;
      return { membro: m, pontos, dias, pratos: meus.length };
    });
  }, [plates, members, periodo, janela.de, janela.ate]);

  const ordenado = useMemo(() => {
    if (!pessoas) return null;
    const chave = placar === "pontos" ? "pontos" : "dias";
    return [...pessoas].sort((a, b) =>
      b[chave] - a[chave]
      || b.pratos - a.pratos
      || a.membro.name.localeCompare(b.membro.name, "pt-BR"));
  }, [pessoas, placar]);

  /* Melhores e piores pratos por nota. Só entram os que já receberam
     notas suficientes: com uma nota só, o pódio vira a opinião de uma
     pessoa apresentada como se fosse a do grupo. */
  const porNota = useMemo(() => {
    const elegiveis = (plates ?? [])
      .filter((p) => p.ratingCount >= MIN_NOTAS && p.ratingAvg != null);
    const melhores = [...elegiveis].sort((a, b) => (b.ratingAvg ?? 0) - (a.ratingAvg ?? 0));
    const piores = [...elegiveis]
      .sort((a, b) => (a.ratingAvg ?? 0) - (b.ratingAvg ?? 0))
      .filter((p) => (p.ratingAvg ?? 10) < VERGONHA_ABAIXO)
      .slice(0, 3);
    return { elegiveis, melhores, piores };
  }, [plates]);

  const linhaPessoa = (
    p: { membro: Member; pontos: number; dias: number; pratos: number },
    posicao: number,
  ) => {
    const valor = placar === "pontos"
      ? `${formatPoints(p.pontos)} ${p.pontos === 1 ? "ponto" : "pontos"}`
      : `${p.dias} ${p.dias === 1 ? "dia ativo" : "dias ativos"}`;
    return (
      <Link key={p.membro.uid} to={`/c/${cid}/u/${p.membro.uid}`}
        className={`rank-row ${p.membro.uid === user?.uid ? "me" : ""}`}>
        <Avatar name={p.membro.name} photo={p.membro.photo} size="md" coroa={coroados.has(p.membro.uid)} />
        <div className="rank-main">
          <div className="rank-name">{p.membro.name}</div>
          <div className="rank-sub">{valor}</div>
        </div>
        <div className="rank-pos">{posicao}º</div>
      </Link>
    );
  };

  const linhaPrato = (p: Plate, posicao: number) => (
    <Link key={p.id} className="rank-row" to={`/c/${cid}/p/${p.id}`}>
      <div className="checkin-thumb guia">
        {p.thumbPath ? <StoragePhoto path={p.thumbPath} alt="" /> : <IconFork size={20} />}
      </div>
      <div className="rank-main">
        <div className="rank-name">{posicao}. {p.title || "Sem título"}</div>
        <div className="rank-sub">{p.authorName} · {rotuloDia(p.day)}</div>
      </div>
      <div className="rank-pos guia">
        <div>⭐ {formatRating(p.ratingAvg)}</div>
        <div className="rank-sub">{p.ratingCount} {p.ratingCount === 1 ? "nota" : "notas"}</div>
      </div>
    </Link>
  );

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title" />
        <span className="topbar-btn" />
      </div>

      <div className="screen-body">
        <div className="page-head">
          <h1>{challenge?.name ?? ""}</h1>
          <p className="sub">
            {periodo === "week" ? "Temporada · " : ""}{formatIntervalo(janela.de, janela.ate, new Date().getFullYear())}
          </p>
          {periodo === "week" && challenge && desafioAtivo(challenge, dayKey()) && (
            <span className="temporada-pill">⏳ {faltaParaAcabar(new Date(), semanaDe(janela.de).ate)}</span>
          )}
        </div>

        <CartaoDoCampeao cid={cid} vigente={vigente}
          fotos={Object.fromEntries(members.map((m) => [m.uid, m.photo]))} />

        <div className="segmented">
          {PERIODOS.map((p) => (
            <button key={p.id} className={p.id === periodo ? "active" : ""}
              onClick={() => { setPeriodo(p.id); setAbertoTudo(false); }}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="segmented on-white">
          {/* Sem notas no desafio, o ranking de pratos (que é por nota) some. */}
          {PLACARES.filter((x) => x.id !== "pratos" || interacaoDe(challenge) !== "ninguem").map(({ id, label, Icone }) => (
            <button key={id} className={id === placar ? "active" : ""}
              onClick={() => { setPlacar(id); setAbertoTudo(false); }}>
              <Icone size={16} /> {label}
            </button>
          ))}
        </div>

        {plates === null || !ordenado ? <EsqueletoDePlacar /> : placar === "pratos" ? (
          porNota.elegiveis.length === 0 ? (
            <Empty emoji="🍽️" title="Ainda sem pódio">
              <p>
                Um prato entra no ranking depois de receber {MIN_NOTAS} notas.
                Abre os pratos da galera e dá nota.
              </p>
            </Empty>
          ) : (
            <>
              <div className="section-label left">🏆 Melhores pratos</div>
              <div className="card lista-crua">
                {(abertoTudo ? porNota.melhores : porNota.melhores.slice(0, 3))
                  .map((p, i) => linhaPrato(p, i + 1))}
              </div>
              {porNota.melhores.length > 3 && (
                <button className="btn btn-white" onClick={() => setAbertoTudo((v) => !v)}>
                  {abertoTudo ? "Mostrar menos" : `Ver todos os ${porNota.melhores.length}`}
                </button>
              )}

              <div className="section-label left">💀 Rango da Vergonha</div>
              {porNota.piores.length ? (
                <>
                  <div className="card lista-crua">
                    {porNota.piores.map((p, i) => linhaPrato(p, i + 1))}
                  </div>
                  <div className="hint-row">
                    Qualquer prato que fechou abaixo de {VERGONHA_ABAIXO} entra
                    aqui. Sem ressentimentos.
                  </div>
                </>
              ) : (
                <div className="card">
                  <div className="comentarios-vazio">
                    Ninguém passou vergonha nesse período. A nota mais baixa
                    ficou acima de {VERGONHA_ABAIXO}.
                  </div>
                </div>
              )}
            </>
          )
        ) : ordenado.length === 0 ? (
          <Empty emoji="🏅" title="Ninguém no placar ainda" />
        ) : (
          <>
            <div className="section-label left">
              {placar === "pontos" ? "Pontos" : "Dias ativos"}
            </div>
            <div className="card lista-crua">
              {(abertoTudo ? ordenado : ordenado.slice(0, 5))
                .map((p, i) => linhaPessoa(p, i + 1))}
            </div>
            <Anuncio posicao="classificacao" />

            {ordenado.length > 5 && (
              <button className="btn btn-white" onClick={() => setAbertoTudo((v) => !v)}>
                {abertoTudo ? "Mostrar menos" : "Todas as classificações"}
              </button>
            )}
            {placar === "pontos" && (
              <div className="hint-row">
                Um prato comprado vale {formatPoints(config.rules.bought)} e um
                cozinhado vale {formatPoints(config.rules.homemade)}, multiplicado
                pelo peso da refeição. A sequência de {config.rules.streakFrom} dias
                multiplica o valor do prato.
              </div>
            )}
          </>
        )}

        <div className="gap" />
      </div>

      <TabBar cid={cid} />
    </main>
  );
}
